import { FormEvent, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { withCurrentSearch } from "@/utils/searchParams";
import { getHorizonScanningApi } from "./api";

export function FeedbackScreen() {
  const [params] = useSearchParams();
  const orgId = params.get("org") ?? "";
  const [score, setScore] = useState<number | null>(null);
  const [why, setWhy] = useState("");
  const [canContact, setCanContact] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const scores = useMemo(() => Array.from({ length: 11 }, (_, i) => i), []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!orgId || score === null) {
      setError("Pick a score from 0 to 10.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await getHorizonScanningApi().submitFeedback({
        orgId,
        score,
        why: why.trim() || undefined,
        canContact,
      });
      setDone(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not send feedback.");
    } finally {
      setBusy(false);
    }
  }

  if (!orgId) {
    return (
      <div className="mx-auto max-w-lg px-5 py-16">
        <h1 className="text-2xl font-bold text-ink">Feedback</h1>
        <p className="mt-3 text-ink-soft">This feedback link is missing an organisation id.</p>
        <Link className="mt-6 inline-block font-semibold text-brand-700" to={withCurrentSearch("/landing/horizonscanning/")}>
          Back to horizon scanning
        </Link>
      </div>
    );
  }

  if (done) {
    return (
      <div className="mx-auto max-w-lg px-5 py-16">
        <h1 className="text-2xl font-bold text-ink">Thank you</h1>
        <p className="mt-3 text-ink-soft">Your feedback helps us improve the digest.</p>
        <Link className="mt-6 inline-block font-semibold text-brand-700" to={withCurrentSearch("/landing/horizonscanning/")}>
          Back to horizon scanning
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-lg px-5 py-16">
      <h1 className="text-2xl font-bold text-ink">Provide feedback</h1>
      <p className="mt-3 text-ink-soft">
        How likely are you to recommend this horizon scanning digest to a colleague? (0 = not at all, 10 = extremely likely)
      </p>
      <form className="mt-8 flex flex-col gap-6" onSubmit={onSubmit}>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Score 0 to 10">
          {scores.map((n) => (
            <button
              key={n}
              type="button"
              className={`h-10 w-10 rounded-lg border text-sm font-semibold ${
                score === n
                  ? "border-brand-500 bg-brand-50 text-brand-800"
                  : "border-slate-300 bg-white text-ink hover:border-brand-400"
              }`}
              onClick={() => setScore(n)}
              aria-pressed={score === n}
            >
              {n}
            </button>
          ))}
        </div>
        <label className="flex flex-col gap-2 text-sm">
          <span className="font-medium text-ink">Why did you give this score? (optional)</span>
          <textarea
            className="r1-field min-h-[6rem]"
            value={why}
            onChange={(e) => setWhy(e.target.value)}
            maxLength={2000}
          />
        </label>
        <label className="flex items-start gap-2 text-sm text-ink">
          <input
            type="checkbox"
            className="mt-1"
            checked={canContact}
            onChange={(e) => setCanContact(e.target.checked)}
          />
          <span>You can contact me about this feedback</span>
        </label>
        {error ? <p className="text-sm text-red-700">{error}</p> : null}
        <button type="submit" className="r1-btn-primary self-start" disabled={busy || score === null}>
          {busy ? "Sending…" : "Send feedback"}
        </button>
      </form>
    </div>
  );
}
